/**
 * DietitianSelectionStep Component
 *
 * Displays matched dietitians and allows patient selection during the
 * self-referral process. Features match scoring, specialization display,
 * and accessible selection interface.
 *
 * Features:
 * - Display of matched dietitians with scores and specializations
 * - Match reason explanations
 * - Accessible selection interface
 * - Loading states and error handling
 * - Responsive design for mobile and desktop
 * - Keyboard navigation support
 *
 * @module components/self-referral/DietitianSelectionStep
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';
import { TitleText, BodyText, Card } from '@/components/ui';
import { AvatarPhoto } from '@/components/ui/AvatarPhoto';
import { cn } from '@/utils/cn';
import { apiService } from '@/services';
import type {
  DietitianSelectionStepProps,
  MatchedDietitian
} from './types';

/**
 * DietitianCard Component
 */
interface DietitianCardProps {
  dietitian: MatchedDietitian;
  onSelect: (dietitian: MatchedDietitian) => void;
  isLoading: boolean;
  isSelected?: boolean;
  photoUrl?: string | null;
}

const DietitianCard: React.FC<DietitianCardProps> = ({
  dietitian,
  onSelect,
  isLoading,
  isSelected = false,
  photoUrl
}) => {
  const { isDark } = useTheme();

  const handleSelect = useCallback(() => {
    if (!isLoading) {
      onSelect(dietitian);
    }
  }, [dietitian, onSelect, isLoading]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect();
    }
  }, [handleSelect]);


  return (
    <Card
      className={cn(
        'p-6 cursor-pointer transition-all duration-200 relative',
        isSelected
          ? isDark
            ? 'ring-2 ring-primary-400 bg-primary-900/20'
            : 'ring-2 ring-primary-500 bg-primary-50'
          : isDark
            ? 'hover:bg-secondary-800 border-secondary-700'
            : 'hover:bg-gray-50 border-gray-200',
        isLoading && 'opacity-50 cursor-not-allowed'
      )}
      role="button"
      tabIndex={isLoading ? -1 : 0}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
      aria-label={`Select ${dietitian.name} as your dietitian`}
      aria-pressed={isSelected}
    >
      {/* Header with avatar and name */}
      <div className="flex items-start gap-4 mb-3">
        {/* Avatar */}
        <div className="flex-shrink-0">
          <AvatarPhoto
            src={photoUrl}
            firstName={dietitian.name.split(' ')[0]}
            lastName={dietitian.name.split(' ').slice(1).join(' ')}
            alt={`${dietitian.name} profile photo`}
            size="lg"
          />
        </div>

        {/* Name and specializations */}
        <div className="flex-1">
          <TitleText level={4} className="mb-2">
            {dietitian.name}
          </TitleText>

          {/* Specializations */}
          {dietitian.specializations && dietitian.specializations.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {dietitian.specializations.map((specialization: string, index: number) => (
                <span
                  key={index}
                  className={cn(
                    'px-2 py-1 rounded-full text-xs font-medium',
                    isDark
                      ? 'bg-primary-900 text-primary-300'
                      : 'bg-primary-100 text-primary-700'
                  )}
                >
                  {specialization}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Match reasons */}
      {dietitian.match_reasons && dietitian.match_reasons.length > 0 && (
        <div className="mb-4">
          <BodyText size="sm" variant="secondary" className="mb-2">
            Why this dietitian is a good match:
          </BodyText>
          <ul className="space-y-1">
            {dietitian.match_reasons.map((reason: string, index: number) => (
              <li key={index} className="flex items-start">
                <span className={cn('mr-2 mt-0.5 leading-none', isDark ? 'text-primary-400' : 'text-primary-600')}>
                  •
                </span>
                <BodyText size="sm" className="flex-1">
                  {reason}
                </BodyText>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Selection indicator */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        {isSelected ? (
          <div className="flex items-center">
            <span className={cn('mr-2', isDark ? 'text-green-400' : 'text-green-600')}>
              ✓
            </span>
            <BodyText className={cn('font-medium', isDark ? 'text-green-400' : 'text-green-600')}>
              Selected
            </BodyText>
          </div>
        ) : (
          <BodyText variant="secondary" size="sm">
            Click to select this dietitian
          </BodyText>
        )}

        {isLoading && (
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
            <BodyText size="sm" variant="secondary">
              Selecting...
            </BodyText>
          </div>
        )}
      </div>
    </Card>
  );
};

/**
 * DietitianSelectionStep Component
 */
export const DietitianSelectionStep: React.FC<DietitianSelectionStepProps> = ({
  dietitians,
  onSelect,
  isLoading,
  error,
  onBack,
  onNext,
  hasSelection = false
}) => {
  const { isDark } = useTheme();
  const [selectedDietitianId, setSelectedDietitianId] = useState<string | null>(null);
  const [photoUrls, setPhotoUrls] = useState<Record<string, string | null>>({});

  /**
   * Fetch dietitian photos
   */
  useEffect(() => {
    const fetchPhotos = async () => {
      if (dietitians.length === 0) return;

      try {
        const userIds = dietitians.map(d => d.dietician_id);
        const photoResponse = await apiService.photo.getPhotoUrls({ user_ids: userIds });
        setPhotoUrls(photoResponse?.photo_urls || {});
      } catch (error) {
        console.warn('Failed to fetch dietitian photos:', error);
        setPhotoUrls({});
      }
    };

    fetchPhotos();
  }, [dietitians]);

  /**
   * Handle dietitian selection
   */
  const handleSelect = useCallback((dietitian: MatchedDietitian) => {
    if (isLoading) return;

    setSelectedDietitianId(dietitian.dietician_id);
    onSelect(dietitian);
  }, [onSelect, isLoading]);

  /**
   * Sort dietitians by overall score (highest first)
   */
  const sortedDietitians = [...dietitians].sort((a, b) => b.score - a.score);

  if (dietitians.length === 0) {
    // Empty state (design-dietitian-accreditation §4.8, OQ9): a hand-off, not an error, in the
    // first slot of the same list container the match cards use. Nothing notifies the practice
    // of an unmatched session, so the copy must not promise a follow-up.
    return (
      <div className="space-y-4">
        <Card
          variant="bordered"
          shadow="none"
          className={cn('border-dashed p-6', isDark && 'border-secondary-400')}
        >
          <div className="flex items-start gap-4">
            <div
              className={cn(
                'flex h-14 w-14 shrink-0 items-center justify-center rounded-full',
                isDark ? 'bg-secondary-800' : 'bg-secondary-50'
              )}
            >
              <Calendar className="h-6 w-6 text-secondary-300" aria-hidden="true" />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <TitleText level={5} className="mb-0">No dietitian to choose just yet</TitleText>
              <BodyText size="sm" variant="secondary">
                There isn't a dietitian available for online booking right now. Please contact
                the practice directly to arrange your appointment.
              </BodyText>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-left">
        <BodyText variant="secondary">
          We've found {dietitians.length} dietitian{dietitians.length !== 1 ? 's' : ''} who match your needs.
          Select the one you'd like to work with.
        </BodyText>
      </div>

      {/* Error message */}
      {error && (
        <Card className={cn(
          'p-4 border-l-4 border-error-500',
          isDark ? 'bg-error-900/20' : 'bg-error-50'
        )}>
          <BodyText variant="primary" className={cn("font-medium", isDark ? 'text-error-400' : 'text-error-600')}>
            <strong>Error:</strong> {error}
          </BodyText>
        </Card>
      )}

      {/* Dietitian cards */}
      <div className="space-y-4">
        {sortedDietitians.map((dietitian) => (
          <DietitianCard
            key={dietitian.dietician_id}
            dietitian={dietitian}
            onSelect={handleSelect}
            isLoading={isLoading}
            isSelected={selectedDietitianId === dietitian.dietician_id}
            photoUrl={photoUrls[dietitian.dietician_id] || null}
          />
        ))}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-8" aria-live="polite">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mr-3"></div>
          <BodyText variant="secondary">
            Processing your selection...
          </BodyText>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700">
        {/* Back button */}
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            disabled={isLoading}
            className={cn(
              'flex items-center px-6 py-3 rounded-lg font-medium transition-all duration-200',
              'border border-gray-300 dark:border-gray-600',
              isDark
                ? 'bg-secondary-800 text-secondary-200 hover:bg-secondary-700 hover:border-gray-500'
                : 'bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400',
              isLoading && 'opacity-50 cursor-not-allowed'
            )}
            aria-label="Go back to patient details"
          >
            <span className="mr-2">←</span>
            Back: Patient Details
          </button>
        )}

        {/* Next button */}
        {onNext && (
          <button
            type="button"
            onClick={onNext}
            disabled={isLoading || !hasSelection}
            className={cn(
              'flex items-center px-6 py-3 rounded-lg font-medium transition-all duration-200',
              hasSelection && !isLoading
                ? isDark
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-primary-600 text-white hover:bg-primary-700'
                : isDark
                  ? 'bg-secondary-700 text-secondary-400 cursor-not-allowed'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            aria-label="Continue to review and submit"
          >
            Next: Review & Submit
            <span className="ml-2">→</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default DietitianSelectionStep;